import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import { LANGUAGE_TO_LOCALE } from '../constants';

const normalizeDate = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
};

const formatDateKey = (value) => {
  const date = normalizeDate(value);
  if (!date) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalizedWeekDays = (locale) => {
  const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const referenceMonday = new Date(Date.UTC(2023, 0, 2)); // Monday
  return Array.from({ length: 7 }, (_, index) => {
    const date = new Date(referenceMonday);
    date.setDate(referenceMonday.getDate() + index);
    return formatter.format(date);
  });
};

const DAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];
const COMPRESSED_HOURS = new Set([2, 3, 4, 5, 6, 7]);
const BASE_TIME_SLOT_HEIGHT_PX = 64;
const OPENING_HOURS_BORDER_COLOR =
  'color-mix(in srgb, var(--chart-3) 55%, var(--chart-3) 45%)';
const OPENING_HOURS_BORDER_PATTERN = `repeating-linear-gradient(90deg, ${OPENING_HOURS_BORDER_COLOR} 0, ${OPENING_HOURS_BORDER_COLOR} 12px, transparent 12px, transparent 22px)`;

const createTimeSlots = (locale) => {
  const formatter = new Intl.DateTimeFormat(locale, { hour: 'numeric' });
  return Array.from({ length: 24 }, (_, hour) => {
    const date = new Date(Date.UTC(2023, 0, 1, hour));
    const isCompressed = COMPRESSED_HOURS.has(hour);
    return {
      hour,
      label: formatter.format(date),
      time24: `${hour.toString().padStart(2, '0')}:00`,
      weight: isCompressed ? 0.5 : 1,
      isCompressed,
    };
  });
};
const SHIFT_CLICK_THRESHOLD_MS = 200;

const ScheduleCalendar = ({
  selectedWeek,
  onWeekChange,
  shifts,
  openingTimes = null,
  onShiftUpdate,
  onShiftSelect,
  employees,
  selectedLocation,
  viewMode = 'week',
  selectedDate,
  onDateChange,
  employeeFilters = {},
}) => {
  const { t, language } = useTranslation(['employeeScheduling']);
  const locale = LANGUAGE_TO_LOCALE[language] ?? LANGUAGE_TO_LOCALE.en;
  const timeSlots = useMemo(() => createTimeSlots(locale), [locale]);
  const weekDays = useMemo(() => getLocalizedWeekDays(locale), [locale]);
  const longDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }),
    [locale]
  );
  const shortDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
      }),
    [locale]
  );
  const rangeEndFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    [locale]
  );
  const [draggedShift, setDraggedShift] = useState(null);
  const [isShiftDragActive, setIsShiftDragActive] = useState(false);
  const [dragPreview, setDragPreview] = useState(null);
  const [isCreatingShift, setIsCreatingShift] = useState(false);
  const [newShiftData, setNewShiftData] = useState(null);
  const [shiftConfirmation, setShiftConfirmation] = useState(null);
  const [isConfirmingShiftMove, setIsConfirmingShiftMove] = useState(false);
  const calendarRef = useRef(null);
  const shiftPressRef = useRef(null);
  const normalizeRoleKey = (value) =>
    value?.toString()?.toLowerCase?.().trim?.();

  const timeSlotBoundaries = useMemo(() => {
    let cumulative = 0;
    return timeSlots.map((slot) => {
      const rawWeight = typeof slot?.weight === 'number' ? slot.weight : 1;
      const normalizedWeight = Number.isFinite(rawWeight)
        ? Math.max(rawWeight, 0)
        : 1;
      const start = cumulative;
      cumulative += normalizedWeight;
      return {
        hour: slot.hour,
        start,
        end: cumulative,
        weight: normalizedWeight,
        isCompressed: slot?.isCompressed ?? false,
      };
    });
  }, [timeSlots]);

  const totalTimeUnits = timeSlotBoundaries.length
    ? timeSlotBoundaries[timeSlotBoundaries.length - 1].end
    : 24;

  const safeTotalTimeUnits = totalTimeUnits > 0 ? totalTimeUnits : 24;
  const calendarMinHeightPx = Math.max(
    safeTotalTimeUnits * BASE_TIME_SLOT_HEIGHT_PX,
    BASE_TIME_SLOT_HEIGHT_PX
  );

  const parseTimeToMinutes = useCallback((timeString) => {
    if (typeof timeString !== 'string') {
      return null;
    }

    const match = timeString.trim().match(/^(\d{1,2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
      return null;
    }

    if (hours < 0 || minutes < 0 || minutes > 59) {
      return null;
    }

    return hours * 60 + minutes;
  }, []);

  const getUnitsForMinutes = useCallback(
    (minutes) => {
      if (!Number.isFinite(minutes)) {
        return 0;
      }

      const safeMinutes = Math.max(0, minutes);
      const hourIndex = Math.floor(safeMinutes / 60);
      if (hourIndex >= timeSlotBoundaries.length) {
        return safeTotalTimeUnits;
      }

      const boundary = timeSlotBoundaries[hourIndex] ?? {
        start: safeTotalTimeUnits,
        end: safeTotalTimeUnits,
        weight: 0,
      };

      const minutesIntoHour = safeMinutes - hourIndex * 60;
      const fraction =
        boundary.weight > 0 ? (minutesIntoHour / 60) * boundary.weight : 0;
      const units = (boundary.start ?? 0) + fraction;

      return Math.min(Math.max(units, 0), safeTotalTimeUnits);
    },
    [safeTotalTimeUnits, timeSlotBoundaries]
  );

  const getBlockPosition = useCallback(
    (startTime, endTime) => {
      const startMinutes = parseTimeToMinutes(startTime);
      const endMinutes = parseTimeToMinutes(endTime);

      const startUnits =
        startMinutes === null ? 0 : getUnitsForMinutes(startMinutes);
      const endUnitsRaw =
        endMinutes === null ? startUnits : getUnitsForMinutes(endMinutes);

      const clampedStart = Math.min(
        Math.max(startUnits, 0),
        safeTotalTimeUnits
      );
      const clampedEnd = Math.min(
        Math.max(endUnitsRaw, clampedStart),
        safeTotalTimeUnits
      );

      const unitsDiff = clampedEnd - clampedStart;
      const minUnits = safeTotalTimeUnits * 0.01;
      const effectiveUnits = unitsDiff > 0 ? unitsDiff : minUnits;

      return {
        top: `${(clampedStart / safeTotalTimeUnits) * 100}%`,
        height: `${(effectiveUnits / safeTotalTimeUnits) * 100}%`,
      };
    },
    [getUnitsForMinutes, parseTimeToMinutes, safeTotalTimeUnits]
  );

  const getWeekDates = (startDate) => {
    const dates = [];
    const start = normalizeDate(startDate);
    if (!start) {
      return dates;
    }

    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      date.setHours(0, 0, 0, 0);
      dates.push(date);
    }

    return dates;
  };

  const getDayDate = (selectedDate) => {
    const normalized = normalizeDate(selectedDate ?? new Date());
    return normalized ?? new Date();
  };

  const weekDates =
    viewMode === 'day'
      ? [getDayDate(selectedDate)]
      : getWeekDates(selectedWeek);
  const getWeekDayIndex = (date) => {
    if (!date) {
      return 0;
    }
    const jsDay = date.getDay();
    return jsDay === 0 ? 6 : jsDay - 1;
  };
  const displayDays =
    viewMode === 'day'
      ? [weekDays?.[getWeekDayIndex(getDayDate(selectedDate))]]
      : weekDays;

  const getRoleIcon = (role) => {
    const roleKey = normalizeRoleKey(role);
    const icons = {
      manager: 'UserCheck',
      'assistant manager': 'Shield',
      server: 'Users',
      cook: 'ChefHat',
      host: 'MessageCircle',
      dishwasher: 'Droplets',
      opening: 'Key',
      serving: 'Clock',
    };
    return icons?.[roleKey] || 'User';
  };

  const getShiftStyle = (shift, overlappingShifts = [], shiftIndex = 0) => {
    const position = getBlockPosition(shift?.startTime, shift?.endTime);

    // Calculate positioning for overlapping shifts
    const totalOverlapping = overlappingShifts?.length;
    const shiftWidth =
      totalOverlapping > 1 ? `${85 / totalOverlapping}%` : '85%';
    const leftOffset =
      totalOverlapping > 1
        ? `${(shiftIndex * 85) / totalOverlapping + 2}%`
        : '2%';

    return {
      ...position,
      minHeight: '40px',
      width: shiftWidth,
      left: leftOffset,
      zIndex: 10 + shiftIndex,
    };
  };

  const getOperatingHoursStyle = (hours) => {
    return {
      ...getBlockPosition(hours?.startTime, hours?.endTime),
      minHeight: '28px',
      backgroundColor: 'var(--background)',
      borderStyle: 'dashed',
      borderWidth: '2px',
      borderColor: OPENING_HOURS_BORDER_COLOR,
      borderImageSource: OPENING_HOURS_BORDER_PATTERN,
      borderImageSlice: 1,
      borderImageWidth: 1,
      borderImageOutset: 0,
      borderImageRepeat: 'round',
      color: 'var(--color-primary)',
      display: 'flex',
      alignItems: 'flex-start',
    };
  };

  const getRoleColor = (role) => {
    const roleKey = normalizeRoleKey(role);
    const colors = {
      manager: 'bg-primary text-primary-foreground',
      'assistant manager': 'bg-secondary text-secondary-foreground',
      server: 'bg-success text-success-foreground',
      cook: 'bg-warning text-warning-foreground',
      host: 'bg-accent text-accent-foreground',
      dishwasher: 'bg-muted text-muted-foreground',
    };
    return colors?.[roleKey] || 'bg-muted text-muted-foreground';
  };

  const handleDragStart = (e, shift) => {
    e?.stopPropagation?.();
    setDraggedShift(shift);
    setIsShiftDragActive(true);
    shiftPressRef.current = null;
    setDragPreview(null);
    if (e?.dataTransfer) {
      try {
        e.dataTransfer.setData(
          'application/json',
          JSON.stringify({ type: 'shift', id: shift?.id ?? null })
        );
      } catch (err) {
        // Some browsers may throw if JSON serialization fails; ignore.
      }
      e.dataTransfer.effectAllowed = 'move';
    }
  };

  const handleDragEnd = () => {
    setDraggedShift(null);
    setDragPreview(null);
    shiftPressRef.current = null;
    setIsShiftDragActive(false);
  };

  const handleDragOver = (e, dayIndex, timeSlot) => {
    e?.preventDefault();
    if (e?.dataTransfer) {
      e.dataTransfer.dropEffect = draggedShift ? 'move' : 'none';
    }

    if (!timeSlot) {
      return;
    }

    setDragPreview((prev) =>
      prev?.dayIndex === dayIndex && prev?.hour === timeSlot?.hour
        ? prev
        : { dayIndex, hour: timeSlot?.hour }
    );
  };

  const handleDragLeave = (e, dayIndex) => {
    const nextTarget = e?.relatedTarget;
    if (nextTarget && e?.currentTarget?.contains?.(nextTarget)) {
      return;
    }

    setDragPreview((prev) => (prev?.dayIndex === dayIndex ? null : prev));
  };

  const handleDrop = async (e, dayIndex, timeSlot) => {
    e?.preventDefault();
    setDragPreview(null);

    if (draggedShift) {
      const parseToMinutes = (timeString) => {
        if (!timeString || typeof timeString !== 'string') {
          return 0;
        }
        const [hours, minutes] = timeString
          .split(':')
          .map((value) => parseInt(value, 10));
        const safeHours = Number.isFinite(hours) ? hours : 0;
        const safeMinutes = Number.isFinite(minutes) ? minutes : 0;
        return safeHours * 60 + safeMinutes;
      };

      const formatMinutes = (minutes) => {
        const clampedMinutes = Math.max(0, Math.min(minutes, 23 * 60 + 59));
        const hourPart = Math.floor(clampedMinutes / 60);
        const minutePart = clampedMinutes % 60;
        return `${hourPart.toString().padStart(2, '0')}:${minutePart
          .toString()
          .padStart(2, '0')}`;
      };

      const targetDate = weekDates?.[dayIndex];
      const updatedDate = formatDateKey(targetDate) || draggedShift?.date;
      const originalDuration = (() => {
        const startMinutes = parseToMinutes(draggedShift?.startTime);
        const endMinutes = parseToMinutes(draggedShift?.endTime);
        const difference = endMinutes - startMinutes;
        return difference > 0 ? difference : 8 * 60;
      })();
      const newStartMinutes = (timeSlot?.hour ?? 0) * 60;
      const newEndMinutes = newStartMinutes + originalDuration;
      const updatedShift = {
        ...draggedShift,
        date: updatedDate ?? draggedShift?.date,
        startTime: timeSlot?.time24,
        endTime: formatMinutes(newEndMinutes),
      };

      const hasMoved =
        draggedShift?.date !== updatedShift.date ||
        draggedShift?.startTime !== updatedShift.startTime ||
        draggedShift?.endTime !== updatedShift.endTime;

      if (!hasMoved) {
        setDraggedShift(null);
        return;
      }

      const confirmMessage = formatTranslation(
        t(
          'employeeScheduling.calendar.confirmMove',
          'Are you sure you want to move this shift to {{date}} starting at {{time}}?'
        ),
        {
          date: targetDate
            ? longDateFormatter.format(targetDate)
            : updatedShift.date,
          time: timeSlot?.label ?? updatedShift.startTime,
        }
      );

      setShiftConfirmation({
        message: confirmMessage,
        updatedShift,
        originalShift: draggedShift,
      });
      setDraggedShift(null);
      setIsShiftDragActive(false);
      return;
    }
  };

  const getPreviewStyle = (hour) => {
    const index = Number.isFinite(hour)
      ? Math.max(0, Math.min(hour, timeSlotBoundaries.length - 1))
      : 0;
    const boundary = timeSlotBoundaries[index];

    if (!boundary) {
      const fallbackHeight = (1 / safeTotalTimeUnits) * 100;
      return { top: '0%', height: `${fallbackHeight}%` };
    }

    const start = boundary.start ?? 0;
    const end = boundary.end ?? start;
    const units = Math.max(end - start, safeTotalTimeUnits * 0.01);

    return {
      top: `${(start / safeTotalTimeUnits) * 100}%`,
      height: `${(units / safeTotalTimeUnits) * 100}%`,
    };
  };

  const handleCancelShiftConfirmation = () => {
    setShiftConfirmation(null);
    setIsConfirmingShiftMove(false);
  };

  const handleConfirmShiftMove = async () => {
    if (!shiftConfirmation) {
      return;
    }

    setIsConfirmingShiftMove(true);

    try {
      await onShiftUpdate?.(
        shiftConfirmation?.updatedShift,
        shiftConfirmation?.originalShift
      );
      setShiftConfirmation(null);
    } catch (err) {
      console.error('Failed to update shift after drag-and-drop', err);
      const errorMessage = t(
        'employeeScheduling.calendar.updateFailed',
        'We could not update the shift. Please try again.'
      );
      setShiftConfirmation((prev) => (prev ? { ...prev, errorMessage } : prev));
    } finally {
      setIsConfirmingShiftMove(false);
    }
  };

  const handleCellClick = (dayIndex, timeSlot) => {
    if (!isCreatingShift) {
      setIsCreatingShift(true);
      setNewShiftData({
        date: formatDateKey(weekDates?.[dayIndex]),
        startTime: timeSlot?.time24,
        dayIndex,
        timeSlot,
      });
    }
  };

  const registerShiftPress = (shift) => {
    if (!shift?.id) {
      shiftPressRef.current = null;
      return;
    }
    shiftPressRef.current = {
      shift,
      timestamp: Date.now(),
    };
  };

  const maybeTriggerShiftSelect = (shift) => {
    const pressState = shiftPressRef.current;
    shiftPressRef.current = null;

    if (isShiftDragActive) {
      return;
    }

    if (!pressState || pressState?.shift?.id !== shift?.id) {
      return;
    }

    const duration = Date.now() - pressState.timestamp;
    if (
      duration <= SHIFT_CLICK_THRESHOLD_MS &&
      typeof onShiftSelect === 'function'
    ) {
      onShiftSelect(shift);
    }
  };

  const getShiftsForDay = (dayIndex) => {
    const dateStr = formatDateKey(weekDates?.[dayIndex]);
    return shifts?.filter((shift) => {
      const matchesDate = shift?.date === dateStr;
      const matchesLocation = shift?.locationId === selectedLocation?.id;
      const matchesStaffFilter =
        employeeFilters?.selectedStaffMembers?.[shift?.employeeId] !== false;
      const roleKey = normalizeRoleKey(shift?.role);
      const matchesRoleFilter = roleKey
        ? employeeFilters?.selectedRoles?.[roleKey] !== false
        : true;

      return (
        matchesDate &&
        matchesLocation &&
        matchesStaffFilter &&
        matchesRoleFilter
      );
    });
  };

  const safeOpeningTimes = useMemo(() => {
    if (!openingTimes || typeof openingTimes !== 'object') {
      return null;
    }
    return openingTimes;
  }, [openingTimes]);

  const getOperatingHoursForDay = (dayIndex) => {
    const date = weekDates?.[dayIndex];
    if (!date || !safeOpeningTimes) {
      return [];
    }

    const dayKey = DAY_KEYS[getWeekDayIndex(date)];
    if (!dayKey) {
      return [];
    }

    const intervals = safeOpeningTimes?.[dayKey];
    if (!Array.isArray(intervals)) {
      return [];
    }

    return intervals
      .map((interval, index) => {
        if (!interval || typeof interval !== 'object') {
          return null;
        }

        const { open, close } = interval;
        if (typeof open !== 'string' || typeof close !== 'string') {
          return null;
        }

        return {
          id: `${dayKey}-${index}`,
          startTime: open,
          endTime: close,
        };
      })
      .filter(Boolean);
  };

  // Helper function to detect overlapping shifts
  const getOverlappingShifts = (shifts, currentShift) => {
    const currentStart = parseInt(currentShift?.startTime?.split(':')?.[0]);
    const currentEnd = parseInt(currentShift?.endTime?.split(':')?.[0]);

    return shifts?.filter((shift) => {
      if (shift?.id === currentShift?.id) return false;
      const shiftStart = parseInt(shift?.startTime?.split(':')?.[0]);
      const shiftEnd = parseInt(shift?.endTime?.split(':')?.[0]);

      // Check if shifts overlap
      return currentStart < shiftEnd && currentEnd > shiftStart;
    });
  };

  // Group overlapping shifts for positioning
  const groupOverlappingShifts = (shifts) => {
    const groups = [];
    const processed = new Set();

    shifts?.forEach((shift) => {
      if (processed?.has(shift?.id)) return;

      const overlapping = getOverlappingShifts(shifts, shift);
      const group = [shift, ...overlapping];

      group?.forEach((s) => processed?.add(s?.id));
      groups?.push(group);
    });

    return groups;
  };

  const navigateWeek = (direction) => {
    const newDate = normalizeDate(selectedWeek);
    if (!newDate) {
      return;
    }

    newDate.setDate(newDate.getDate() + direction * 7);
    onWeekChange(newDate);
  };

  const navigateDay = (direction) => {
    const currentDate = normalizeDate(selectedDate || new Date());
    if (!currentDate) {
      return;
    }

    currentDate.setDate(currentDate.getDate() + direction);
    onDateChange?.(currentDate);
  };

  const goToToday = () => {
    const today = normalizeDate(new Date());
    if (!today) {
      return;
    }

    if (viewMode === 'day') {
      onDateChange?.(today);
      return;
    }

    const monday = new Date(today);
    monday.setDate(today.getDate() - today.getDay() + 1);
    monday.setHours(0, 0, 0, 0);
    onWeekChange(monday);
  };

  const formatDateRange = () => {
    if (viewMode === 'day') {
      const date = getDayDate(selectedDate);
      return date ? longDateFormatter.format(date) : '';
    }
    const start = weekDates?.[0];
    const end = weekDates?.[6];
    const startStr = start ? shortDateFormatter.format(start) : '';
    const endStr = end ? rangeEndFormatter.format(end) : '';
    return startStr && endStr
      ? `${startStr} - ${endStr}`
      : startStr || endStr || '';
  };

  return (
    <div
      className="bg-card rounded-lg border border-border shadow-elevation-1 flex flex-col"
      style={{ height: 'calc(100vh - 280px)', minHeight: '600px' }}
    >
      {/* Calendar Header */}
      <div className="flex items-center justify-between p-4 border-b border-border flex-shrink-0">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                viewMode === 'day' ? navigateDay(-1) : navigateWeek(-1)
              }
              iconName="ChevronLeft"
              iconSize={16}
            >
              {t('employeeScheduling.calendar.previous')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                viewMode === 'day' ? navigateDay(1) : navigateWeek(1)
              }
              iconName="ChevronRight"
              iconPosition="right"
              iconSize={16}
            >
              {t('employeeScheduling.calendar.next')}
            </Button>
          </div>
          <Button variant="ghost" size="sm" onClick={goToToday}>
            {t('employeeScheduling.calendar.today')}
          </Button>
        </div>

        <div className="text-center">
          <h2 className="text-lg font-semibold text-foreground">
            {formatDateRange()}
          </h2>
          <p className="text-sm text-muted-foreground">
            {selectedLocation?.name}
          </p>
        </div>

        <div className="flex items-center space-x-2">
          <Button variant="outline" size="sm" iconName="Download" iconSize={16}>
            {t('employeeScheduling.calendar.export')}
          </Button>
          <Button variant="default" size="sm" iconName="Send" iconSize={16}>
            {t('employeeScheduling.calendar.publish')}
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        <div
          className={`grid min-w-full h-full ${
            viewMode === 'day' ? 'grid-cols-2' : 'grid-cols-8'
          }`}
        >
          {/* Time Column */}
          <div className="border-r border-border bg-muted/30 flex flex-col">
            <div className="h-12 border-b border-border flex items-center justify-center text-xs font-medium text-muted-foreground">
              {t('employeeScheduling.calendar.time')}
            </div>
            <div
              className="relative flex-1"
              style={{ minHeight: `${calendarMinHeightPx}px` }}
            >
              {timeSlots?.map((slot, index) => {
                const boundary = timeSlotBoundaries[index];
                const start = boundary?.start ?? 0;
                const end = boundary?.end ?? start;
                const heightUnits = Math.max(
                  end - start,
                  safeTotalTimeUnits * 0.01
                );
                const heightPercent = (heightUnits / safeTotalTimeUnits) * 100;
                const compressedClasses = slot?.isCompressed
                  ? 'bg-muted/40 text-muted-foreground'
                  : 'text-muted-foreground';

                return (
                  <div
                    key={slot?.hour}
                    className={`border-b border-border flex items-start justify-center pt-1 text-xs ${compressedClasses}`}
                    style={{ height: `${heightPercent}%` }}
                  >
                    {slot?.label}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Day Columns */}
          {displayDays?.map((day, dayIndex) => {
            const dayShifts = getShiftsForDay(dayIndex);
            const shiftGroups = groupOverlappingShifts(dayShifts);
            const dayOperatingHours = getOperatingHoursForDay(dayIndex);

            return (
              <div
                key={dayIndex}
                className={`border-r border-border relative ${
                  viewMode === 'day' ? 'min-w-96' : 'min-w-32'
                }`}
              >
                {/* Day Header */}
                <div className="h-12 border-b border-border flex flex-col items-center justify-center bg-muted/20">
                  <div className="text-xs font-medium text-muted-foreground">
                    {day}
                  </div>
                  <div className="text-sm font-semibold text-foreground">
                    {weekDates?.[dayIndex]?.getDate()}
                  </div>
                </div>

                {/* Time Slots */}
                <div
                  className="relative"
                  style={{ minHeight: `${calendarMinHeightPx}px` }}
                  onDragLeave={(e) => handleDragLeave(e, dayIndex)}
                >
                  {timeSlots?.map((slot, index) => {
                    const boundary = timeSlotBoundaries[index];
                    const start = boundary?.start ?? 0;
                    const end = boundary?.end ?? start;
                    const heightUnits = Math.max(
                      end - start,
                      safeTotalTimeUnits * 0.01
                    );
                    const heightPercent =
                      (heightUnits / safeTotalTimeUnits) * 100;
                    const compressedClasses = slot?.isCompressed
                      ? 'bg-muted/40'
                      : '';

                    return (
                      <div
                        key={slot?.hour}
                        className={`border-b border-border hover:bg-muted/20 cursor-pointer transition-colors ${compressedClasses}`}
                        style={{ height: `${heightPercent}%` }}
                        onDragOver={(e) => handleDragOver(e, dayIndex, slot)}
                        onDrop={(e) => handleDrop(e, dayIndex, slot)}
                        onClick={() => handleCellClick(dayIndex, slot)}
                      />
                    );
                  })}

                  {dragPreview?.dayIndex === dayIndex ? (
                    <div
                      className="absolute inset-x-1 pointer-events-none z-20"
                      style={getPreviewStyle(dragPreview?.hour)}
                    >
                      <div className="h-full rounded-md border-2 border-primary/60 bg-primary/15 flex items-center px-2 text-xs font-semibold text-primary">
                        {timeSlots?.find(
                          (slot) => slot?.hour === dragPreview?.hour
                        )?.label ?? ''}
                      </div>
                    </div>
                  ) : null}

                  {/* Operating Hours Background */}
                  {dayOperatingHours?.map((hours) => (
                    <div
                      key={hours?.id}
                      className="absolute left-0 right-0 rounded pointer-events-none shadow-sm"
                      style={getOperatingHoursStyle(hours)}
                    >
                      <div className="p-2 text-xs font-medium opacity-80">
                        <div className="flex items-center space-x-1">
                          <Icon name="Key" size={12} className="opacity-80" />
                          <span>
                            {t(
                              'employeeScheduling.calendar.openingHoursLegend',
                              'Opening hours'
                            )}
                          </span>
                        </div>
                        <div className="text-xs opacity-80">
                          {hours?.startTime} - {hours?.endTime}
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Employee Shifts */}
                  {shiftGroups?.map((group, groupIndex) =>
                    group?.map((shift, shiftIndex) => (
                      <div
                        key={shift?.id}
                        className={`absolute rounded px-2 py-1 cursor-move shadow-sm border ${getRoleColor(
                          shift?.role
                        )} transition-transform hover:scale-105`}
                        style={getShiftStyle(shift, group, shiftIndex)}
                        draggable
                        onDragStart={(e) => handleDragStart(e, shift)}
                        onDragEnd={handleDragEnd}
                        onMouseDown={(e) => {
                          e?.stopPropagation?.();
                          registerShiftPress(shift);
                        }}
                        onMouseUp={(e) => {
                          e?.stopPropagation?.();
                          maybeTriggerShiftSelect(shift);
                        }}
                        onMouseLeave={() => {
                          shiftPressRef.current = null;
                        }}
                        onTouchStart={(e) => {
                          e?.stopPropagation?.();
                          registerShiftPress(shift);
                        }}
                        onTouchEnd={(e) => {
                          e?.stopPropagation?.();
                          maybeTriggerShiftSelect(shift);
                        }}
                        onTouchCancel={() => {
                          shiftPressRef.current = null;
                        }}
                        onClick={(e) => {
                          e?.stopPropagation?.();
                          maybeTriggerShiftSelect(shift);
                        }}
                      >
                        <div className="flex items-center space-x-1 mb-1">
                          <Icon name={getRoleIcon(shift?.role)} size={12} />
                          <div className="text-xs font-medium truncate flex-1">
                            {shift?.employeeName}
                          </div>
                        </div>
                        <div className="text-xs opacity-80 truncate">
                          {shift?.role}
                        </div>
                        <div className="text-xs opacity-80">
                          {shift?.startTime} - {shift?.endTime}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="p-4 border-t border-border bg-muted/20 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-foreground">
              {t('employeeScheduling.calendar.rolesLabel')}
            </span>
            <div className="flex items-center space-x-3 flex-wrap">
              {['manager', 'assistant manager', 'server', 'cook', 'host']?.map(
                (role) => (
                  <div key={role} className="flex items-center space-x-1">
                    <div className={`w-3 h-3 rounded ${getRoleColor(role)}`} />
                    <Icon
                      name={getRoleIcon(role)}
                      size={12}
                      className="text-muted-foreground"
                    />
                    <span className="text-xs text-muted-foreground capitalize">
                      {t(`employeeScheduling.roles.${role}`, role)}
                    </span>
                  </div>
                )
              )}
            </div>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-3">
              <span className="text-sm font-medium text-foreground">
                {t('employeeScheduling.calendar.hoursLabel')}
              </span>
              <div className="flex items-center space-x-1">
                <div
                  className="w-3 h-3 rounded"
                  style={{
                    backgroundColor: 'var(--background)',
                    borderStyle: 'dashed',
                    borderWidth: '2px',
                    borderColor: OPENING_HOURS_BORDER_COLOR,
                    borderImageSource: OPENING_HOURS_BORDER_PATTERN,
                    borderImageSlice: 1,
                    borderImageWidth: 1,
                    borderImageOutset: 0,
                    borderImageRepeat: 'round',
                  }}
                />
                <Icon name="Key" size={12} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">
                  {t(
                    'employeeScheduling.calendar.openingHoursLegend',
                    'Opening hours'
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Icon name="Users" size={14} />
              <span>
                {formatTranslation(t('employeeScheduling.calendar.shifts'), {
                  count:
                    shifts?.filter((s) => {
                      const matchesLocation =
                        s?.locationId === selectedLocation?.id;
                      const matchesStaffFilter =
                        employeeFilters?.selectedStaffMembers?.[
                          s?.employeeId
                        ] !== false;
                      const matchesRoleFilter =
                        employeeFilters?.selectedRoles?.[
                          normalizeRoleKey(s?.role)
                        ] !== false;
                      return (
                        matchesLocation &&
                        matchesStaffFilter &&
                        matchesRoleFilter
                      );
                    })?.length ?? 0,
                })}
              </span>
            </div>
            <div className="flex items-center space-x-1">
              <Icon name="Clock" size={14} />
              <span>
                {formatTranslation(t('employeeScheduling.calendar.hours'), {
                  hours:
                    shifts
                      ?.filter((s) => {
                        const matchesLocation =
                          s?.locationId === selectedLocation?.id;
                        const matchesStaffFilter =
                          employeeFilters?.selectedStaffMembers?.[
                            s?.employeeId
                          ] !== false;
                        const matchesRoleFilter =
                          employeeFilters?.selectedRoles?.[
                            normalizeRoleKey(s?.role)
                          ] !== false;
                        return (
                          matchesLocation &&
                          matchesStaffFilter &&
                          matchesRoleFilter
                        );
                      })
                      ?.reduce((total, shift) => {
                        const start = parseInt(
                          shift?.startTime?.split(':')?.[0]
                        );
                        const end = parseInt(shift?.endTime?.split(':')?.[0]);
                        return total + (end - start);
                      }, 0) ?? 0,
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
      {shiftConfirmation ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-card border border-border rounded-lg shadow-elevation-2 max-w-lg w-full">
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  {t(
                    'employeeScheduling.calendar.moveShiftTitle',
                    'Confirm Shift Move'
                  )}
                </h3>
                <p className="mt-2 text-sm text-muted-foreground whitespace-pre-line">
                  {shiftConfirmation?.message}
                </p>
              </div>
              {shiftConfirmation?.errorMessage ? (
                <div className="rounded-md border border-error/20 bg-error/10 px-4 py-2 text-sm text-error">
                  {shiftConfirmation?.errorMessage}
                </div>
              ) : null}
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelShiftConfirmation}
                  disabled={isConfirmingShiftMove}
                >
                  {t('employeeScheduling.calendar.cancelMove', 'Cancel')}
                </Button>
                <Button
                  size="sm"
                  onClick={handleConfirmShiftMove}
                  loading={isConfirmingShiftMove}
                >
                  {t(
                    'employeeScheduling.calendar.confirmMoveCta',
                    'Confirm Move'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ScheduleCalendar;
